import { Component, computed, inject, signal, ViewEncapsulation, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { ThemeService } from '../../core/services/theme.service';
import { I18nService } from '../../core/services/i18n.service';
import { LayoutService } from '../../core/services/layout.service';
import { AuthService } from '../../core/services/auth.service';
import { PermissionService } from '../../core/services/permission.service';
import { IconPipe } from '../../shared/pipes/icon.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { ToastComponent } from '../../shared/toast/toast.component';

interface NavChild { key: string; route: string; icon: string; }
interface NavItem { key: string; icon: string; route?: string; children?: NavChild[]; }
interface NavGroup { label?: string; items: NavItem[]; }

const NAV: NavGroup[] = [
  {
    label: 'nav.manage',
    items: [
      { key: 'nav.dashboard', icon: 'dashboard', route: '/dashboard' },
      { key: 'nav.games', icon: 'games', route: '/games' },
      { key: 'nav.categories', icon: 'category', route: '/categories' },
      { key: 'nav.challenges', icon: 'trophy', route: '/challenges' },
      { key: 'nav.rewards', icon: 'gift', route: '/rewards' },
      { key: 'nav.leaderboards', icon: 'trophy', route: '/leaderboards' },
      {
        key: 'nav.users', icon: 'users',
        children: [
          { key: 'nav.players', route: '/players', icon: 'users' },
          { key: 'nav.admins', route: '/admins', icon: 'shield' },
        ],
      },
    ],
  },
  {
    items: [
      { key: 'nav.claims', icon: 'truck', route: '/claims' },
      { key: 'nav.moderation', icon: 'shield', route: '/moderation' },
      { key: 'nav.support', icon: 'mail', route: '/support' },
    ],
  },

  {
    items: [
      {
        key: 'nav.promotion', icon: 'gift',
        children: [
          { key: 'nav.homebanners', route: '/promotion/home-banners', icon: 'image' },
          { key: 'nav.adbanner', route: '/promotion/ad-banners', icon: 'monitor' },
          { key: 'nav.pushnotif', route: '/promotion/push-notifications', icon: 'bell' },
        ],
      },
    ],
  },
  {
    items: [
      {
        key: 'nav.settings', icon: 'settings',
        children: [
          { key: 'nav.langsettings', route: '/settings/language', icon: 'globe' },
          { key: 'nav.cmspages', route: '/settings/cms-pages', icon: 'copy' },
          { key: 'nav.faqs', route: '/settings/faqs', icon: 'chat' },
          { key: 'nav.emailsettings', route: '/settings/email', icon: 'mail' },
          { key: 'nav.globalsettings', route: '/settings/global', icon: 'settings' },
          { key: 'nav.badges', route: '/settings/badges', icon: 'star' },
          { key: 'nav.collections', route: '/settings/collections', icon: 'copy' },
          { key: 'nav.achievements', route: '/settings/achievements', icon: 'trophy' },
        ],
      },
    ],
  },
];

interface SearchablePage { key: string; route: string; icon: string; }

function flattenNav(groups: NavGroup[]): SearchablePage[] {
  const pages: SearchablePage[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      if (item.route) pages.push({ key: item.key, route: item.route, icon: item.icon });
      for (const child of item.children ?? []) {
        pages.push({ key: child.key, route: child.route, icon: child.icon });
      }
    }
  }
  return pages;
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, FormsModule, IconPipe, TranslatePipe, ConfirmDialogComponent, ToastComponent],
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AdminLayoutComponent implements OnDestroy {
  protected themeService  = inject(ThemeService);
  protected i18nService   = inject(I18nService);
  protected layoutService = inject(LayoutService);
  private router          = inject(Router);
  private authService     = inject(AuthService);
  private permissionService = inject(PermissionService);

  readonly nav = computed(() => {
    const perm = this.permissionService;
    return NAV
      .map(group => ({
        ...group,
        items: group.items
          .map(item => {
            if (item.children) {
              const children = item.children.filter(c => perm.canAccessRoute(c.route));
              return children.length ? { ...item, children } : null;
            }
            return item.route && !perm.canAccessRoute(item.route) ? null : item;
          })
          .filter((i): i is NavItem => i !== null),
      }))
      .filter(group => group.items.length > 0);
  });

  readonly admin = this.authService.getAdmin();
  readonly adminInitials = this.admin?.fullName
    ? this.admin.fullName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : 'AD';
  readonly sidebarOpen = signal(false);
  readonly collapsed = signal(false);
  readonly expandedKeys = signal(this.getInitialExpanded());
  readonly searchOpen = signal(false);
  readonly searchQuery = signal('');
  private readonly searchablePages = computed(() => flattenNav(this.nav()));
  readonly searchResults = computed(() => {
    const q = this.searchQuery().trim().toLowerCase();
    if (!q) return [];
    return this.searchablePages()
      .filter(p => this.i18nService.t(p.key).toLowerCase().includes(q))
      .slice(0, 8);
  });

  readonly flyoutItem = signal<NavItem | null>(null);
  readonly flyoutTop = signal(0);
  readonly tooltipKey = signal<string | null>(null);
  readonly tooltipTop = signal(0);
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  readonly userMenuOpen = signal(false);
  readonly changePasswordOpen = signal(false);
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';

  readonly riyadhTime = signal(this.formatRiyadhTime());
  readonly riyadhDate = signal(this.formatRiyadhDate());
  private readonly clockTimer = setInterval(() => {
    this.riyadhTime.set(this.formatRiyadhTime());
    this.riyadhDate.set(this.formatRiyadhDate());
  }, 1000);

  private formatRiyadhTime(): string {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: 'Asia/Riyadh',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  private formatRiyadhDate(): string {
    return new Date().toLocaleDateString('en-US', {
      timeZone: 'Asia/Riyadh',
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });
  }

  private getInitialExpanded(): Set<string> {
    const url = this.router.url;
    const s = new Set<string>();
    for (const group of NAV) {
      for (const item of group.items) {
        if (item.children?.some(c => url.startsWith(c.route))) s.add(item.key);
      }
    }
    return s;
  }

  logout(): void { this.authService.logout(); }

  toggleUserMenu(): void { this.userMenuOpen.set(!this.userMenuOpen()); }
  closeUserMenu(): void { this.userMenuOpen.set(false); }

  openChangePassword(): void {
    this.closeUserMenu();
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.changePasswordOpen.set(true);
  }

  closeChangePassword(): void { this.changePasswordOpen.set(false); }

  toggleTheme(): void { this.themeService.toggle(); }
  toggleSidebar(): void { this.sidebarOpen.set(!this.sidebarOpen()); }
  closeSidebar(): void { this.sidebarOpen.set(false); }
  toggleCollapse(): void { this.collapsed.set(!this.collapsed()); }
  setLang(lang: 'en' | 'ar'): void { this.i18nService.setLang(lang); }

  openSearch(): void { this.searchOpen.set(true); }
  closeSearch(input: HTMLInputElement): void {
    setTimeout(() => { if (!input.value) this.searchOpen.set(false); }, 150);
  }

  onSearchInput(value: string): void { this.searchQuery.set(value); }

  goToPage(page: SearchablePage): void {
    this.router.navigateByUrl(page.route);
    this.searchQuery.set('');
    this.searchOpen.set(false);
  }

  toggleItem(key: string): void {
    this.expandedKeys.update(s => {
      const next = new Set(s);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  isItemOpen(key: string): boolean { return this.expandedKeys().has(key); }

  isChildActive(item: NavItem): boolean {
    return item.children?.some(c => this.router.url.startsWith(c.route)) ?? false;
  }

  onNavEnter(key: string, el: EventTarget | null): void {
    if (!this.collapsed() || !(el instanceof Element)) return;
    const rect = el.getBoundingClientRect();
    this.tooltipTop.set(rect.top + rect.height / 2);
    this.tooltipKey.set(key);
  }

  onNavLeave(): void { this.tooltipKey.set(null); }

  onParentEnter(item: NavItem, el: EventTarget | null): void {
    if (!this.collapsed() || !item.children || !(el instanceof Element)) return;
    if (this.hideTimer) { clearTimeout(this.hideTimer); this.hideTimer = null; }
    const rect = el.getBoundingClientRect();
    this.flyoutTop.set(rect.top);
    this.flyoutItem.set(item);
  }

  onParentLeave(): void {
    this.hideTimer = setTimeout(() => this.flyoutItem.set(null), 120);
  }

  onFlyoutEnter(): void {
    if (this.hideTimer) { clearTimeout(this.hideTimer); this.hideTimer = null; }
  }

  onFlyoutLeave(): void {
    this.flyoutItem.set(null);
  }

  ngOnDestroy(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    clearInterval(this.clockTimer);
  }
}
